import React, { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HASH = /^#book$/;
const empty = { name: "", email: "", phone: "", business: "", message: "" };

const Field = ({ id, label, ...p }) => (
  <label className="block">
    <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink-faint)] mb-1.5">{label}</span>
    {p.as === "textarea" ? (
      <textarea id={id} data-testid={`book-${id}`} rows={3} className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-[15px] text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--blue)]/30" {...p} />
    ) : (
      <input id={id} data-testid={`book-${id}`} className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-[15px] text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--blue)]/30" {...p} />
    )}
  </label>
);

const RequestForm = () => {
  const [f, setF] = useState(empty);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setErr("");
    try {
      await axios.post(`${API}/call-requests`, f);
      setState("sent");
      setF(empty);
    } catch (e2) {
      setState("idle");
      setErr(e2.response?.status === 422 ? "Please add your name and a valid email." : "Something went wrong. Please try again in a moment.");
    }
  };

  if (state === "sent")
    return (
      <div data-testid="book-success" className="py-12 text-center">
        <CheckCircle2 className="w-10 h-10 mx-auto text-[var(--blue)]" />
        <p className="font-serif text-[24px] text-[var(--ink)] mt-4">Thanks, we&apos;ll be in touch soon.</p>
        <p className="text-[14px] text-[var(--ink-muted)] mt-2">A co-founder will reach out within one business day.</p>
      </div>
    );

  return (
    <form data-testid="book-form" onSubmit={submit} className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field id="name" label="Name *" required value={f.name} onChange={set("name")} />
        <Field id="email" label="Email *" type="email" required value={f.email} onChange={set("email")} />
        <Field id="phone" label="Phone" value={f.phone} onChange={set("phone")} />
        <Field id="business" label="Business" value={f.business} onChange={set("business")} />
      </div>
      <Field id="message" label="What would you like help with?" as="textarea" value={f.message} onChange={set("message")} />
      {err && <p data-testid="book-error" className="text-[13px] text-[#B4232A]">{err}</p>}
      <button type="submit" data-testid="book-submit" disabled={state === "sending"} className="btn-cobalt w-full rounded-full text-[15px] font-medium py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {state === "sending" ? "Sending…" : "Request my free call"}
      </button>
    </form>
  );
};

const BOOKING_SRC = "https://api.leadconnectorhq.com/widget/booking/qGlVKuw8or2j367KMdBM";
const EMBED_JS = "https://link.msgsndr.com/js/form_embed.js";

const useEmbedScript = (on) =>
  useEffect(() => {
    if (!on || document.querySelector(`script[src="${EMBED_JS}"]`)) return;
    const s = document.createElement("script");
    s.src = EMBED_JS;
    s.type = "text/javascript";
    document.body.appendChild(s);
  }, [on]);

const SectionLabel = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-3 font-mono text-[10.5px] tracking-[0.2em] uppercase text-[var(--ink-faint)]">
    <Icon className="w-4 h-4 text-[#A8823A]" />
    {children}
  </div>
);

export default function BookCallDialog() {
  const [open, setOpen] = useState(false);
  useEmbedScript(open);

  useEffect(() => {
    const check = () => HASH.test(window.location.hash) && setOpen(true);
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, []);

  const close = () => {
    setOpen(false);
    if (HASH.test(window.location.hash)) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="book-dialog" className="max-w-[640px] w-[94vw] max-h-[92vh] overflow-y-auto rounded-2xl border border-[var(--line)] bg-white p-0 [&>button]:text-white [&>button]:z-10">
        <header className="relative bg-[var(--night)] text-white px-6 sm:px-8 pt-7 pb-6 overflow-hidden">
          <div className="absolute -top-24 -right-16 w-[260px] h-[260px] orb opacity-60" />
          <span className="relative font-mono text-[10.5px] tracking-[0.22em] uppercase text-[#E8CE95]">Free 30-minute call</span>
          <DialogTitle className="relative font-serif font-medium text-[28px] leading-tight mt-1.5 text-white tracking-normal">Let&apos;s talk about your business.</DialogTitle>
          <DialogDescription className="relative mt-1.5 text-[14px] text-white/70">Pick a time, or leave a message and we&apos;ll reach out.</DialogDescription>
        </header>
        <div className="px-6 sm:px-8 py-6">
          <SectionLabel icon={CalendarDays}>Pick a time</SectionLabel>
          <iframe
            data-testid="book-calendar"
            id="qGlVKuw8or2j367KMdBM_booking"
            title="Book a free call"
            src={BOOKING_SRC}
            scrolling="yes"
            style={{ width: "100%", height: 720, border: "none", overflow: "auto" }}
            className="rounded-xl bg-white"
          />
          <div className="mt-8 pt-6 border-t border-[var(--line)]">
            <SectionLabel icon={Send}>Or send us a message</SectionLabel>
            <RequestForm />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
